import { useRef, useState } from 'react';

import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ModelSelect } from '@/components/service-history/ModelSelect';
import { openServiceDocument, openSummaryPdf } from '@/components/service-history/open-document';
import { ServiceFactsView } from '@/components/service-history/ServiceFactsView';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useAddServiceDocuments,
  useDeleteServiceVehicle,
  useEditServiceSummary,
  useRegenerateServiceSummary,
  useRemoveServiceDocument,
  useServiceVehicle,
} from '@/hooks/useServiceHistory';

import { SERVICE_HISTORY_DEFAULT_MODEL, SERVICE_HISTORY_MODELS } from '@repo/shared-types';

import type { ServiceHistoryModel, ServiceSummary } from '@repo/shared-types';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*';

const isKnownModel = (model: string | undefined): model is ServiceHistoryModel =>
  SERVICE_HISTORY_MODELS.some((option) => option.value === model);

const statusBadge = (summary: ServiceSummary) => {
  if (summary.status === 'failed') return <Badge variant="destructive">Failed</Badge>;
  if (summary.is_current) return <Badge>Current</Badge>;
  return <Badge variant="secondary">Older</Badge>;
};

/** One vehicle: current summary, its source files, every AI run, and controls to change them. */
export const ServiceHistoryVehiclePage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: vehicle, isLoading } = useServiceVehicle(id);
  usePageTitle(vehicle ? `Service History · ${vehicle.display_registration}` : 'Service History');

  const [chosenModel, setChosenModel] = useState<ServiceHistoryModel | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  // Remove takes two clicks: the first arms it, the second removes.
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Summary editing: null = not editing, otherwise the text being edited.
  const [editText, setEditText] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);

  const addDocuments = useAddServiceDocuments(id);
  const removeDocument = useRemoveServiceDocument(id);
  const regenerate = useRegenerateServiceSummary(id);
  const editSummary = useEditServiceSummary(id);
  const deleteVehicle = useDeleteServiceVehicle();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const busy =
    addDocuments.isPending ||
    removeDocument.isPending ||
    regenerate.isPending ||
    editSummary.isPending;

  if (isLoading) return <p className="text-muted-foreground p-6 text-sm">Loading…</p>;
  if (!vehicle) return <p className="text-muted-foreground p-6 text-sm">Vehicle not found.</p>;

  // Default the picker to the model that made the current summary.
  const currentModel = vehicle.current?.model_name;
  const model =
    chosenModel ?? (isKnownModel(currentModel) ? currentModel : SERVICE_HISTORY_DEFAULT_MODEL);

  const activeDocuments = vehicle.documents.filter((doc) => !doc.deleted_at);
  const documentNames = new Map(vehicle.documents.map((doc) => [doc.id, doc.file_name]));
  // Selecting an older run shows it in place of the current one.
  const shown =
    vehicle.history.find((summary) => summary.id === viewingId) ?? vehicle.current ?? null;

  // Only the current, successful summary can be edited.
  const canEdit = !!shown && shown.is_current && shown.status === 'ready';

  const handleSaveEdit = async () => {
    if (editText === null) return;
    await editSummary.mutateAsync(editText.trim());
    setEditText(null);
  };

  // Two clicks: the first arms the button, the second deletes and returns to the list.
  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    await deleteVehicle.mutateAsync(vehicle.id);
    navigate('/service-history');
  };

  const handleRemove = (documentId: string) => {
    if (confirmRemoveId !== documentId) {
      setConfirmRemoveId(documentId);
      return;
    }
    setConfirmRemoveId(null);
    removeDocument.mutate(documentId);
  };

  return (
    <div className="max-w-6xl space-y-4 p-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/service-history">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Service History
        </Link>
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-mono text-2xl font-semibold">{vehicle.display_registration}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <ModelSelect value={model} onChange={setChosenModel} disabled={busy} />
          <Button
            variant="outline"
            onClick={() => {
              setViewingId(null);
              regenerate.mutate(model);
            }}
            disabled={busy || activeDocuments.length === 0}
          >
            {regenerate.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {regenerate.isPending ? 'Reading…' : 'Regenerate'}
          </Button>
          <Button
            variant={confirmDelete ? 'destructive' : 'outline'}
            onClick={handleDelete}
            onBlur={() => setConfirmDelete(false)}
            disabled={busy || deleteVehicle.isPending}
            title="Deletes the car, its files, summaries and PDFs"
          >
            {deleteVehicle.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" />
            )}
            {confirmDelete ? 'Click again to delete' : 'Delete vehicle'}
          </Button>
        </div>
      </div>
      {busy && (
        <p className="text-muted-foreground text-xs">
          The AI is reading the files. This can take a minute or two.
        </p>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">
              {shown?.is_current ? 'Current summary' : shown ? 'Older summary' : 'Summary'}
            </CardTitle>
            {canEdit && editText === null && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditText(shown.summary ?? '')}
                  disabled={busy}
                >
                  <Pencil className="mr-1 h-4 w-4" />
                  Edit summary
                </Button>
                <Button variant="outline" size="sm" onClick={() => openSummaryPdf(vehicle.id)}>
                  <Download className="mr-1 h-4 w-4" />
                  Download PDF
                </Button>
              </div>
            )}
          </div>
          {shown && (
            <CardDescription>
              {shown.model_name} · generated {new Date(shown.generated_at).toLocaleString('en-GB')}
              {shown.generated_by && ` by ${shown.generated_by}`} · read{' '}
              {shown.source_document_ids.length} file(s):{' '}
              {shown.source_document_ids
                .map((docId) => documentNames.get(docId) ?? 'removed file')
                .join(', ')}
              {viewingId && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 pl-2"
                  onClick={() => setViewingId(null)}
                >
                  Back to current
                </Button>
              )}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {shown?.status === 'failed' && (
            <p className="text-destructive text-sm">This run failed: {shown.error}</p>
          )}
          {shown && shown.status !== 'failed' && (
            <div className="space-y-3">
              {editText !== null && (
                <div className="space-y-2">
                  <textarea
                    value={editText}
                    onChange={(event) => setEditText(event.target.value)}
                    rows={6}
                    aria-label="Summary"
                    className="border-input bg-background focus-visible:ring-ring w-full rounded-md border p-3 text-sm leading-relaxed focus-visible:ring-1 focus-visible:outline-none"
                  />
                  <p className="text-muted-foreground text-xs">
                    This is the text the agent reads to customers and puts in the PDF.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleSaveEdit}
                      disabled={editText.trim().length < 10 || editSummary.isPending}
                    >
                      {editSummary.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditText(null)}
                      disabled={editSummary.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {shown.edited_at && editText === null && (
                <p className="text-muted-foreground text-xs">
                  Edited {new Date(shown.edited_at).toLocaleString('en-GB')}
                  {shown.edited_by && ` by ${shown.edited_by}`}
                  {shown.ai_summary && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 pl-2 text-xs"
                      onClick={() => setShowOriginal(!showOriginal)}
                    >
                      {showOriginal ? 'Hide AI original' : 'Show AI original'}
                    </Button>
                  )}
                </p>
              )}
              {showOriginal && shown.ai_summary && editText === null && (
                <p className="bg-muted rounded-md p-3 text-sm whitespace-pre-line">
                  {shown.ai_summary}
                </p>
              )}
              <ServiceFactsView
                summary={shown}
                onOpenFile={openServiceDocument}
                hideSummaryText={editText !== null}
              />
            </div>
          )}
          {!shown && (
            <p className="text-muted-foreground text-sm">
              No current summary. Add files or press Regenerate.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Files ({activeDocuments.length})</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {addDocuments.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-1 h-4 w-4" />
            )}
            Add files
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              if (files.length) addDocuments.mutate({ files, model });
            }}
          />
        </CardHeader>
        <CardContent>
          {activeDocuments.length === 0 ? (
            <p className="text-muted-foreground text-sm">No files.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {activeDocuments.map((doc) => (
                <li key={doc.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                  <button
                    type="button"
                    className="text-primary flex-1 truncate text-left hover:underline"
                    onClick={() => openServiceDocument(doc.id)}
                  >
                    {doc.file_name}
                  </button>
                  <span className="text-muted-foreground text-xs">
                    {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}
                    {doc.uploaded_by && ` · ${doc.uploaded_by}`}
                  </span>
                  <Button
                    variant={confirmRemoveId === doc.id ? 'destructive' : 'ghost'}
                    size="sm"
                    aria-label={`Remove ${doc.file_name}`}
                    title="The summary is rebuilt from the remaining files"
                    onClick={() => handleRemove(doc.id)}
                    onBlur={() => setConfirmRemoveId(null)}
                    disabled={busy}
                  >
                    <Trash2 className="h-4 w-4" />
                    {confirmRemoveId === doc.id && <span className="ml-1">Remove?</span>}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Generation history</CardTitle>
          <CardDescription>Every AI run on this car. Click a row to view it.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Generated</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Files read</TableHead>
                <TableHead>Services</TableHead>
                <TableHead>Tokens (in / out)</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicle.history.map((summary) => (
                <TableRow
                  key={summary.id}
                  className={`cursor-pointer ${shown?.id === summary.id ? 'bg-muted' : ''}`}
                  onClick={() => setViewingId(summary.id)}
                >
                  <TableCell className="text-xs whitespace-nowrap">
                    {new Date(summary.generated_at).toLocaleString('en-GB')}
                  </TableCell>
                  <TableCell className="text-xs">
                    {summary.model_name}
                    {summary.edited_at && ' · edited'}
                  </TableCell>
                  <TableCell>{statusBadge(summary)}</TableCell>
                  <TableCell className="text-xs">{summary.source_document_ids.length}</TableCell>
                  <TableCell className="text-xs">
                    {summary.key_facts?.total_services ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs">
                    {summary.input_tokens !== null
                      ? `${summary.input_tokens.toLocaleString('en-GB')} / ${(summary.output_tokens ?? 0).toLocaleString('en-GB')}`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {summary.generated_by ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
